import { useRef, useState } from 'react';
import { Alert, Button, Stack, TextField } from '@mui/material';
import {
  useCampaignControllerCampaignQuery,
  useCampaignUpdateControllerCampaignUpdateMutation,
  useUserMeControllerUserMeQuery,
  type CampaignListItemDto,
} from '@shared/services/apiService';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { CampaignPicker } from './CampaignPicker';
import { CompanyDashboard } from './CompanyDashboard';
const Reservation = ({
  id,
  onDirtyChange,
}: {
  id: string;
  onDirtyChange: (v: boolean) => void;
}) => {
  const campaign = useCampaignControllerCampaignQuery({ id });
  const me = useUserMeControllerUserMeQuery();
  const [save, { isLoading }] = useCampaignUpdateControllerCampaignUpdateMutation();
  const [value, setValue] = useState<string>();
  const [error, setError] = useState('');
  const busy = useRef(false);
  const locked =
    me.data?.role === 'viewer' ||
    !me.data?.privileges.includes('campaign.manage') ||
    campaign.currentData?.status !== 'inactive';
  return (
    <Stack gap={2}>
      {campaign.isError && <Alert severity="error">Unable to load current reservation.</Alert>}
      <TextField
        label="Campaign reserved concurrent calls"
        type="number"
        value={value ?? campaign.currentData?.reservedLimit ?? ''}
        disabled={locked || isLoading}
        slotProps={{ htmlInput: { min: 0, step: 1 } }}
        onChange={(e) => {
          setValue(e.target.value);
          onDirtyChange(true);
        }}
      />
      {error && <Alert severity="error">{error}</Alert>}
      <Button
        disabled={
          locked ||
          isLoading ||
          value === undefined ||
          !/^\d+$/.test(value) ||
          !Number.isSafeInteger(Number(value))
        }
        onClick={() => {
          if (busy.current || value === undefined) return;
          busy.current = true;
          void save({ id, campaignUpdateRequestDto: { reservedLimit: Number(value) } })
            .unwrap()
            .then(() => {
              setValue(undefined);
              onDirtyChange(false);
              setError('');
            })
            .catch((e) => setError(getApiErrorMessage(e, 'Unable to save the reservation.')))
            .finally(() => {
              busy.current = false;
            });
        }}>
        Save campaign reservation
      </Button>
      {locked && (
        <Alert severity="info">
          Reservation changes require an inactive campaign and campaign management permission.
        </Alert>
      )}
    </Stack>
  );
};
export const ConcurrencyWorkspace = ({
  onDirtyChange,
  requestNavigation,
}: {
  onDirtyChange: (v: boolean) => void;
  requestNavigation: (action: () => void) => void;
}) => {
  const [campaign, setCampaign] = useState<CampaignListItemDto | null>(null);
  return (
    <Stack gap={3}>
      <CompanyDashboard concurrency />
      <CampaignPicker
        value={campaign}
        onChange={(c) =>
          requestNavigation(() => {
            onDirtyChange(false);
            setCampaign(c);
          })
        }
      />
      {campaign && <Reservation key={campaign.id} id={campaign.id} onDirtyChange={onDirtyChange} />}
    </Stack>
  );
};
